import { useState } from 'react';
import { CreditCard, Wallet, AlertTriangle, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface SubscriptionRequiredModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  message?: string;
  action?: 'add_funds' | 'increase_limit' | 'add_payment_method';
}

export function SubscriptionRequiredModal({
  isOpen,
  onClose,
  title,
  message,
  action = 'add_funds',
}: SubscriptionRequiredModalProps) {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const defaults: Record<string, { title: string; message: string; buttonText: string }> = {
    add_funds: {
      title: 'Funds Required',
      message: 'Your account balance is $0.00. Please add funds or enable auto-recharge on the Billing page to continue using services.',
      buttonText: 'Add Funds',
    },
    increase_limit: {
      title: 'Usage Limit Reached',
      message: 'You have reached your monthly spending limit. Please increase your limit or wait until next month.',
      buttonText: 'Manage Billing',
    },
    add_payment_method: {
      title: 'Card Details Required',
      message: 'To create deployments and use our GPU services, you need to add your card details first. Go to the Billing page to set up your payment method.',
      buttonText: 'Add Card Details',
    },
  };

  const config = defaults[action] || defaults.add_funds;

  const handleGoToBilling = () => {
    onClose();
    navigate('/billing');
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-[60] animate-fade-in"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 pointer-events-none">
        <div
          className="card max-w-md w-full p-6 pointer-events-auto animate-scale-in"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1 hover:bg-background-hover rounded-md transition-colors text-foreground-secondary hover:text-foreground"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Icon */}
          <div className="flex justify-center mb-4">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
              action === 'add_payment_method' ? 'bg-yellow-500/10' : 'bg-accent/10'
            }`}>
              {action === 'add_payment_method' ? (
                <CreditCard className="w-8 h-8 text-yellow-500" />
              ) : action === 'increase_limit' ? (
                <AlertTriangle className="w-8 h-8 text-accent" />
              ) : (
                <Wallet className="w-8 h-8 text-accent" />
              )}
            </div>
          </div>

          {/* Title */}
          <h2 className="text-xl font-semibold text-foreground text-center mb-2">
            {title || config.title}
          </h2>

          {/* Message */}
          <p className="text-sm text-foreground-secondary text-center mb-6">
            {message || config.message}
          </p>

          {/* Steps for payment method */}
          {action === 'add_payment_method' && (
            <div className="mb-6 p-4 bg-yellow-500/5 border border-yellow-500/20 rounded-lg space-y-3">
              <p className="text-xs font-medium text-yellow-600 dark:text-yellow-400 uppercase tracking-wider mb-2">
                How to get started:
              </p>
              <div className="space-y-2">
                <div className="flex items-start gap-3 text-sm text-foreground-secondary">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-yellow-500/20 text-yellow-600 dark:text-yellow-400 flex items-center justify-center text-xs font-bold">1</span>
                  <span>Go to the <strong>Billing</strong> page</span>
                </div>
                <div className="flex items-start gap-3 text-sm text-foreground-secondary">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-yellow-500/20 text-yellow-600 dark:text-yellow-400 flex items-center justify-center text-xs font-bold">2</span>
                  <span>Click <strong>"Add Card"</strong> and enter your card details</span>
                </div>
                <div className="flex items-start gap-3 text-sm text-foreground-secondary">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-yellow-500/20 text-yellow-600 dark:text-yellow-400 flex items-center justify-center text-xs font-bold">3</span>
                  <span>Add funds to your account balance</span>
                </div>
                <div className="flex items-start gap-3 text-sm text-foreground-secondary">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-yellow-500/20 text-yellow-600 dark:text-yellow-400 flex items-center justify-center text-xs font-bold">4</span>
                  <span>Come back and create your deployment</span>
                </div>
              </div>
            </div>
          )}

          {/* Features for add_funds */}
          {action === 'add_funds' && (
            <div className="mb-6 p-4 bg-background-secondary rounded-lg space-y-2">
              <p className="text-xs font-medium text-foreground-tertiary uppercase tracking-wider mb-2">
                Pay-as-you-go includes:
              </p>
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 text-sm text-foreground-secondary">
                  <div className="w-1.5 h-1.5 rounded-full bg-accent" />
                  <span>GPU compute resources</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-foreground-secondary">
                  <div className="w-1.5 h-1.5 rounded-full bg-accent" />
                  <span>API access and deployments</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-foreground-secondary">
                  <div className="w-1.5 h-1.5 rounded-full bg-accent" />
                  <span>Storage and bandwidth</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-foreground-secondary">
                  <div className="w-1.5 h-1.5 rounded-full bg-accent" />
                  <span>No monthly subscription fees</span>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <button onClick={onClose} className="btn-secondary flex-1">
              Cancel
            </button>
            <button onClick={handleGoToBilling} className="btn-primary flex-1">
              {config.buttonText}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// Hook to handle billing errors
export function useSubscriptionError() {
  const [showModal, setShowModal] = useState(false);
  const [modalConfig, setModalConfig] = useState<{
    title?: string;
    message?: string;
    action?: 'add_funds' | 'increase_limit' | 'add_payment_method';
  }>({});

  const handleError = (error: any) => {
    if (
      error?.statusCode === 402 ||
      error?.details?.error === 'Funds required' ||
      error?.details?.error === 'Usage limit reached' ||
      error?.details?.error === 'Active subscription required'
    ) {
      const requiresAction = error.details?.requiresAction || 'add_funds';
      setModalConfig({
        title: error.details?.error || 'Funds Required',
        message: error.message || error.details?.message,
        action: requiresAction as 'add_funds' | 'increase_limit' | 'add_payment_method',
      });
      setShowModal(true);
      return true;
    }
    return false;
  };

  const closeModal = () => {
    setShowModal(false);
    setModalConfig({});
  };

  return {
    showSubscriptionModal: showModal,
    subscriptionModalConfig: modalConfig,
    handleSubscriptionError: handleError,
    closeSubscriptionModal: closeModal,
  };
}
